import type {
  DecisaoCategoriaTransferencia,
  DecisaoProjecaoTransferencia,
  OpcaoProjecao,
  ProjecaoEscopo,
} from "@/modules/financeiro-obras";
import type { OpcoesGravadas } from "../../../hooks/use-opcoes-projecao";

export interface CategoriaDaOrigem {
  distribuicao: OpcaoProjecao | null;
  curva: OpcaoProjecao | null;
}

export interface OrigemProjecaoTransferencia {
  servicos: CategoriaDaOrigem;
  materiais: CategoriaDaOrigem | null;
  usaCustoPadrao: boolean;
  fatorIdade: number | null;
}

export function origemDaProjecao(
  projecao: ProjecaoEscopo | null | undefined,
): OrigemProjecaoTransferencia | null {
  if (!projecao) return null;
  const categoria = (c: { distribuicao: OpcaoProjecao | null; curva: OpcaoProjecao | null }) => ({
    distribuicao: c.distribuicao,
    curva: c.curva,
  });
  return {
    servicos: categoria(projecao.servicos),
    materiais: projecao.materiais ? categoria(projecao.materiais) : null,
    usaCustoPadrao: projecao.usaCustoPadrao,
    fatorIdade: projecao.fatorIdade,
  };
}

export function gravadasDaOrigem(
  origem: OrigemProjecaoTransferencia | null,
): OpcoesGravadas | undefined {
  if (!origem) return undefined;
  return {
    distribuicoes: {
      SERVICOS: origem.servicos.distribuicao,
      MATERIAIS: origem.materiais?.distribuicao,
    },
    curvas: { SERVICOS: origem.servicos.curva, MATERIAIS: origem.materiais?.curva },
  };
}

export interface RascunhoCategoria {
  distribuicaoId: string | null;
  curvaId: string | null;
}

export interface RascunhoAjuste {
  servicos: RascunhoCategoria;
  materiais: RascunhoCategoria | null;
  fator: "MANTER" | "ATUALIZAR";
  fatorPercentual: number | null;
}

export const FATOR_MIN = 0;
export const FATOR_MAX = 100;

export const percentualValido = (valor: number | null): valor is number =>
  valor !== null && valor >= FATOR_MIN && valor <= FATOR_MAX;

const daOrigem = (c: CategoriaDaOrigem): RascunhoCategoria => ({
  distribuicaoId: c.distribuicao?.id ?? null,
  curvaId: c.curva?.id ?? null,
});

const daDecisao = (
  base: RascunhoCategoria,
  decisao: DecisaoCategoriaTransferencia | undefined,
): RascunhoCategoria => ({
  distribuicaoId: decisao?.distribuicaoId ?? base.distribuicaoId,
  curvaId: decisao?.curvaId ?? base.curvaId,
});

export function rascunhoInicial(
  origem: OrigemProjecaoTransferencia,
  decisao: DecisaoProjecaoTransferencia | null,
): RascunhoAjuste {
  return {
    servicos: daDecisao(daOrigem(origem.servicos), decisao?.servicos),
    materiais: origem.materiais ? daDecisao(daOrigem(origem.materiais), decisao?.materiais) : null,
    fator: decisao?.fator === "ATUALIZAR" ? "ATUALIZAR" : "MANTER",
    fatorPercentual: decisao?.fator === "ATUALIZAR" ? (decisao.fatorPercentual ?? null) : null,
  };
}

const diferencas = (
  base: RascunhoCategoria,
  atual: RascunhoCategoria,
): DecisaoCategoriaTransferencia | undefined => {
  const decisao: DecisaoCategoriaTransferencia = {
    ...(atual.distribuicaoId && atual.distribuicaoId !== base.distribuicaoId
      ? { distribuicaoId: atual.distribuicaoId }
      : {}),
    ...(atual.curvaId && atual.curvaId !== base.curvaId ? { curvaId: atual.curvaId } : {}),
  };
  return Object.keys(decisao).length > 0 ? decisao : undefined;
};

export function decisaoDoRascunho(
  origem: OrigemProjecaoTransferencia,
  rascunho: RascunhoAjuste,
): DecisaoProjecaoTransferencia | null {
  const servicos = diferencas(daOrigem(origem.servicos), rascunho.servicos);
  const materiais =
    origem.materiais && rascunho.materiais
      ? diferencas(daOrigem(origem.materiais), rascunho.materiais)
      : undefined;
  const decisao: DecisaoProjecaoTransferencia = {
    ...(servicos ? { servicos } : {}),
    ...(materiais ? { materiais } : {}),
    ...(rascunho.fator === "ATUALIZAR" ? { fator: "ATUALIZAR" as const } : {}),
    ...(rascunho.fator === "ATUALIZAR" && percentualValido(rascunho.fatorPercentual)
      ? { fatorPercentual: rascunho.fatorPercentual }
      : {}),
  };
  return Object.keys(decisao).length > 0 ? decisao : null;
}

export const PARAMETROS_MANTIDOS = "Mantidos da origem";

const mudancasDaCategoria = (nome: string, decisao: DecisaoCategoriaTransferencia | undefined) => {
  const campos = [
    decisao?.distribuicaoId ? "distribuição" : null,
    decisao?.curvaId ? "curva" : null,
  ].filter((campo) => campo !== null);
  return campos.length > 0 ? [`${nome}: ${campos.join(" e ")}`] : [];
};

export function resumoDaDecisao(decisao: DecisaoProjecaoTransferencia | null | undefined): string {
  if (!decisao) return PARAMETROS_MANTIDOS;
  const partes = [
    ...mudancasDaCategoria("Serviços", decisao.servicos),
    ...mudancasDaCategoria("Materiais", decisao.materiais),
    ...(decisao.fator === "ATUALIZAR"
      ? [
          decisao.fatorPercentual === undefined
            ? "Fator atualizado"
            : `Fator ${decisao.fatorPercentual.toLocaleString("pt-BR")}%`,
        ]
      : []),
  ];
  return partes.length > 0 ? partes.join(" · ") : PARAMETROS_MANTIDOS;
}
